<template>
    <card-normal without-padding>
        <flex-container :padding="Size.md">
            <flex-container :size="5">
                <text-heading>
                    Recente notities
                </text-heading>
            </flex-container>

            <flex-container :size="5" justify-content="end">
                <input-group :model-value="'Alles'" inline>
                    <template #input>
                        <select-input
                            :items="[
                                'Alles',
                            ]"
                        />
                    </template>
                </input-group>
            </flex-container>
        </flex-container>

        <list-container>
            <list-row
                v-for="comment in filteredComments"
                :key="comment.id.toString()"
            >
                <flex-container :padding="Size.md">
                    <comment-block
                        :comment="comment"
                        :lead="comment.lead"
                        :delete-comment="deleteComment"
                    />
                </flex-container>
            </list-row>
        </list-container>

        <list-message-empty v-if="commentCollection?.data.value.length === 0">
            <slot name="empty-message">
                <text-decorator :size="FontSize.sm">
                    Er zijn geen notities gevonden.
                </text-decorator>
            </slot>
        </list-message-empty>

        <list-footer>
            <template #default>
                <contact-create-comment-collapse>
                    <span>Opmerking toevoegen</span>
                </contact-create-comment-collapse>
            </template>

            <template #right>
                <list-footer-action-show-more
                    v-if="commentCollection?.data.value.length > 4"
                    @click="showAll = !showAll"
                >
                    <template v-if="showAll">
                        Bekijk minder
                    </template>
                    <template v-else>
                        Bekijk alle {{ commentCollection?.count.value }}
                    </template>
                </list-footer-action-show-more>
            </template>
        </list-footer>
    </card-normal>
</template>

<script setup lang="ts">
    import {
        CardNormal,
        FlexContainer,
        FontSize,
        InputGroup,
        SelectInput,
        TextHeading,
        Size,
        ListContainer,
        ListRow,
        ListFooterActionShowMore,
        ListFooter,
    } from '@keyvue/one-ui';

    import { computed, ref } from 'vue';
    import CommentBlock from '@components/Blocks/CommentBlock.vue';
    import { CommentId, Comment } from '@keyvue/one-api-client';
    import { CommentAPI } from '@keyvue/one-api-client-legacy';
    import useNotify from '@composables/useNotify';
    import useClient from '@composables/useClient';
    import ContactCreateCommentCollapse from '@components/Collapses/ContactCreateCommentCollapse.vue';
    import useEventBus from '@composables/useEventBus';

    export interface Props {
        filters: object,
    }

    const props = defineProps<Props>();

    const { notifySaving, notifySaved } = useNotify();
    const { getReactiveCollection } = useClient();
    const eventBus = useEventBus();

    const commentCollection = await getReactiveCollection(Comment, props.filters);
    const availableComments = computed(() => commentCollection.data.value);
    const sortedComments = computed(() => (
        [...availableComments.value].sort((a, b) => ((new Date(b.createdAt)).valueOf() - (new Date(a.createdAt)).valueOf()))
    ));
    const filteredComments = computed(() => (showAll.value ? sortedComments.value : [...sortedComments.value].splice(0, 4)));

    const showAll = ref(false);

    const deleteComment = async (commentId: CommentId) => {
        const closeNotification = notifySaving(undefined, 0);
        await CommentAPI.delete(commentId.value);
        await commentCollection.load();
        eventBus.dispatch('resource-manipulated', 'Comment');
        notifySaved();
        if (closeNotification) {
            closeNotification();
        }
    };

    eventBus.subscribe('resource-manipulated', async (params) => {
        if (params === 'Comment') {
            await commentCollection.load();
        }
    });
</script>
